import React from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';

import Dashboard from 'containers/Dashboard';
import SurveyTemplateListContainer from 'containers/SurveyTemplateList';
import Business from 'containers/Business';
import Questions from 'containers/Questions';
import Users from 'containers/Users';
import Login from 'containers/Login';
import PrivacyPolicy from 'containers/PrivacyPolicy';
import TermsOfUse from 'containers/TermsOfUse';
import LandingPage from 'containers/LandingPage';
import Analytics from 'containers/Analytics';
import SoonoSocial from 'containers/SoonoSocial';

import DashboardLayout from 'components/DashboardLayout';
import LandingPageLayout from 'components/LandingPage/Layout';
import BusinessCustomSurveyReport from 'components/businesses/tabs/reports/CustomSurveyPublicReport';
import BusinessTab from 'components/businesses/tabs/BusinessTab';
import BusinessAddInformation from 'components/businesses/BusinessAddInformation';
import {
  BusinessUser,
  BusinessDetails,
  BusinessCustomSurvey,
  BusinessQuestion,
  BusinessRedemption,
  BusinessRequestAddOns,
} from 'components/Business-admin/MenuRedirect';
import ResetPassword from 'components/users/ResetPassword';
import UpdatePassword from 'components/users/UpdatePassword';
import BusinessUserInvitation from 'components/businesses/tabs/business-users/invitation/BusinessUserInvitation';
import AddNewBusinessUser from 'components/businesses/tabs/business-users/AddNewBusinessUser';
import AddSurveyTemplate from 'components/categories/AddSurveyTemplate';
import UserEmailConfirmation from 'components/users/EmailConfirmation';
import {
  ROUTES,
  insertIdtoUrl,
  TRIAL_SIGNUP_ROUTES,
  ONBOARDING_ROUTES,
  RENEW_SUBSCRIPTION_ROUTES,
} from 'constants/Routes.js';
import {
  getRole,
  getToken,
  getBusinessId,
} from 'constants/localStorageManager.js';
import { USERS } from 'constants/Users.js';
import SoonoToaster from 'utils/SoonoToaster';
import '../utils/browserInactivity';

import BusinessDashboard from 'containers/BusinessDashboard';
import AddonsRequest from 'components/Addons/Requests';
import TrialContactDetails from 'components/transactions/TrialContactDetails';
import TrialBusinessDetails from 'components/transactions/TrialBusinessDetails';
import TrialPlan from 'components/transactions/TrialPlan';
import SelectPlan from 'components/transactions/SelectPlan';
import SelectSurvey from 'components/Onboarding/SelectSurvey';
import PreviewReports from 'components/Onboarding/PreviewReports';
import UserDetails from 'containers/UserDetails';
import UpgradeModal from 'components/Modal/UpgradeModal/UpgradeModal';
import PaymentStep from 'components/transactions/PaymentStep';
import PaymentStepAddress from 'components/transactions/PaymentStepAddress';
import RenewPlan from 'components/plan/RenewPlan';
import UserSurveyReward from 'components/userSurveyReward';
import CustomSurveyForm from 'components/customSurvey/CustomSurveyForm';
import BillingActivity from './billingActivity';

const AnalyticsTracker = () => <Route component={Analytics} />;

const OnboardingRoute = ({ component: UserObject, ...rest }) => (
  <Route
    {...rest}
    render={matchProps =>
      !getToken() || !getRole() ? (
        <Redirect to="/" />
      ) : (
        <React.Fragment>
          {UserObject.map((Obj, index) => {
            if (Obj.role.includes(getRole()))
              return <Obj.component {...matchProps} key={index} />;
          })}
        </React.Fragment>
      )
    }
  />
);

const DashboardRoute = ({ component: UserObject, ...rest }) => (
  <Route
    {...rest}
    render={matchProps =>
      !getToken() || !getRole() ? (
        <Redirect to="/" />
      ) : (
        <DashboardLayout>
          {UserObject.map((Obj, index) => {
            if (Obj.role.includes(getRole()))
              return <Obj.component {...matchProps} key={index} />;
          })}
        </DashboardLayout>
      )
    }
  />
);

const AuthPageRoute = ({ component: Component, loginContainer, ...rest }) => (
  <Route
    {...rest}
    render={matchProps => (
      <LandingPageLayout loginContainer={loginContainer}>
        <Component {...matchProps} />
      </LandingPageLayout>
    )}
  />
);

const LandingRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={matchProps =>
      !getToken() || !getRole() ? (
        <Component {...matchProps} />
      ) : getRole() === USERS.ADMIN ? (
        <Redirect to={ROUTES.DASHBOARD_INDEX} />
      ) : getBusinessId() ? (
        <Redirect
          to={insertIdtoUrl(ROUTES.BUSINESS_DASHBOARD_INDEX, getBusinessId())}
        />
      ) : (
        <Component {...matchProps} />
      )
    }
  />
);

const surveyTemplateRoutes = () => (
  <Switch>
    <DashboardRoute
      exact
      path={ROUTES.ADD_NEW_SURVEY_TEMPLATE}
      component={[{ role: [USERS.ADMIN], component: AddSurveyTemplate }]}
    />
    <DashboardRoute
      exact
      path={ROUTES.EDIT_SURVEY_TEMPLATE}
      component={[{ role: [USERS.ADMIN], component: AddSurveyTemplate }]}
    />
    <DashboardRoute
      exact
      path={ROUTES.SURVEY_TEMPLATE_INDEX}
      component={[
        { role: [USERS.ADMIN], component: SurveyTemplateListContainer },
      ]}
    />
  </Switch>
);

/*signup router*/
const signUpRoutes = () => (
  <div>
    <Switch>
      <Route
        exact
        path={TRIAL_SIGNUP_ROUTES.CONTACT_DETAILS}
        component={TrialContactDetails}
      />
      <Route path={TRIAL_SIGNUP_ROUTES.SELECT_PLANS} component={SelectPlan} />
      <Route
        path={TRIAL_SIGNUP_ROUTES.BUSINESS_DETAILS}
        component={TrialBusinessDetails}
      />
      <Route path={TRIAL_SIGNUP_ROUTES.TRIAL_PLANS} component={TrialPlan} />
      <Route
        path={TRIAL_SIGNUP_ROUTES.PAYMENT_DETAILS}
        component={PaymentStep}
      />
      <Route
        path={TRIAL_SIGNUP_ROUTES.PAYMENT_ADDRESS}
        component={PaymentStepAddress}
      />
    </Switch>
  </div>
);

const renewRoutes = () => (
  <div>
    <Switch>
      <Route
        path={RENEW_SUBSCRIPTION_ROUTES.SELECT_PLANS}
        render={props => <RenewPlan {...props} renew />}
      />
      <Route
        path={RENEW_SUBSCRIPTION_ROUTES.PAYMENT_DETAILS}
        render={props => <PaymentStep {...props} renew />}
      />
      <Route
        path={RENEW_SUBSCRIPTION_ROUTES.PAYMENT_ADDRESS}
        render={props => <PaymentStepAddress {...props} renew />}
      />
    </Switch>
  </div>
);

const App = () => (
  <BrowserRouter>
    <div className="App">
      <SoonoToaster />
      <AnalyticsTracker />
      <Route
        exact
        path={ROUTES.USER_SURVEY_REWARD}
        component={UserSurveyReward}
      />
      <DashboardRoute
        exact
        path={ROUTES.DASHBOARD_INDEX}
        component={[{ role: [USERS.ADMIN], component: Dashboard }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.BUSINESS_USER_DETAIL}
        component={[
          {
            role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
            component: UserDetails,
          },
        ]}
      />
      <Switch>
        <Route
          exact
          path={ROUTES.EMAIL_CONFIRMATION}
          component={UserEmailConfirmation}
        />
        <DashboardRoute
          exact
          path={ROUTES.USER_SHOW}
          component={[
            {
              role: [USERS.ADMIN, USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: UserDetails,
            },
          ]}
        />
      </Switch>
      <DashboardRoute
        exact
        path={ROUTES.BUSINESS_DASHBOARD_INDEX}
        component={[
          {
            role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
            component: BusinessDashboard,
          },
        ]}
      />
      <DashboardRoute
        exact
        path={ROUTES.BUSINESS_INDEX}
        component={[{ role: [USERS.ADMIN], component: Business }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.CATEGORIES_QUESTIONS}
        component={[{ role: [USERS.ADMIN], component: Questions }]}
      />
      <Route
        component={surveyTemplateRoutes}
        path={ROUTES.SURVEY_TEMPLATE_INDEX}
      />
      <DashboardRoute
        exact
        path={ROUTES.BUSINESS_FILTER}
        component={[{ role: [USERS.ADMIN], component: Business }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.BUSINESS_CATEGORY_FILTER}
        component={[{ role: [USERS.ADMIN], component: Business }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.QUESTION_INDEX}
        component={[{ role: [USERS.ADMIN], component: Questions }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.CATEGORY_REPORTS}
        component={[{ role: [USERS.ADMIN], component: Dashboard }]}
      />
      <DashboardRoute
        exact
        path={ROUTES.REPORT_INDEX}
        component={[{ role: [USERS.ADMIN], component: Dashboard }]}
      />
      <Switch>
        <DashboardRoute
          exact
          path={ROUTES.ADD_NEW_BUSINESS}
          component={[
            { role: [USERS.ADMIN], component: BusinessAddInformation },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.BUSINESS_DETAIL}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: BusinessDetails,
            },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.EDIT_BUSINESS_BASIC_INFO}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: BusinessDetails,
            },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.REQUEST_BUSINESS_ADD_ONS}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: BusinessRequestAddOns,
            },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.USER_INDEX}
          component={[{ role: [USERS.ADMIN], component: Users }]}
        />
        <DashboardRoute
          exact
          path={ROUTES.QUESTION_INDEX}
          component={[{ role: [USERS.ADMIN], component: Questions }]}
        />
        <DashboardRoute
          exact
          path={ROUTES.CUSTOM_SURVEY_LIST}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: BusinessCustomSurvey,
            },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.USER_INDEX}
          component={[{ role: [USERS.ADMIN], component: Users }]}
        />
        <DashboardRoute
          exact
          path={ROUTES.QUESTION_INDEX}
          component={[{ role: [USERS.ADMIN], component: Questions }]}
        />
        <DashboardRoute
          exact
          path={ROUTES.CUSTOM_SURVEY_ADD}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: BusinessCustomSurvey,
            },
          ]}
        />
        <DashboardRoute
          exact
          path={ROUTES.CUSTOM_SURVEY_EDIT}
          component={[
            { role: [USERS.ADMIN], component: BusinessTab },
            {
              role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
              component: CustomSurveyForm,
            },
          ]}
        />
        <Switch>
          <DashboardRoute
            exact
            path={ROUTES.QUESTION_LIST}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessQuestion,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.CUSTOM_QUESTION_LIST}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessQuestion,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.CUSTOM_SURVEY_QUESTIONS}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessQuestion,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.REPORT_LIST}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_SURVEY_EMAIL_CONFIG}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_COMMENT_EMAIL_CONFIG}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_SURVEY_FEEDBACKS}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.CUSTOM_SURVEY_FEEDBACKS}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_SMS_ALERT_CONFIG}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.CUSTOM_REPORT_LIST}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.CUSTOM_SURVEY_REPORTS}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessDashboard,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.USERS_LIST}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              { role: [USERS.BUSINESS_ADMIN], component: BusinessUser },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_USER_INVITATION_NEW}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              { role: [USERS.BUSINESS_ADMIN], component: BusinessUser },
            ]}
          />

          <DashboardRoute
            exact
            path={ROUTES.REWARD_ISSUED}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BusinessRedemption,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.REWARD_REDEEMED}
            component={[
              { role: [USERS.ADMIN], component: BusinessTab },
              {
                role: [USERS.BUSINESS_ADMIN],
                component: BusinessRedemption,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.BUSINESS_BILLING_ACTIVITY}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: BillingActivity,
              },
            ]}
          />
          <DashboardRoute
            exact
            path={ROUTES.ADDONS_REQUEST}
            component={[{ role: [USERS.ADMIN], component: AddonsRequest }]}
          />
          <OnboardingRoute
            exact
            path={ONBOARDING_ROUTES.STEP_ONE}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: SelectSurvey,
              },
            ]}
          />
          <OnboardingRoute
            exact
            path={ONBOARDING_ROUTES.STEP_TWO}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: SelectSurvey,
              },
            ]}
          />
          <OnboardingRoute
            exact
            path={ONBOARDING_ROUTES.STEP_THREE}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: SelectSurvey,
              },
            ]}
          />
          <OnboardingRoute
            exact
            path={ONBOARDING_ROUTES.STEP_FOUR}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: PreviewReports,
              },
            ]}
          />
          <Route
            exact
            path={ROUTES.RENEW_SUBSCRIPTION}
            component={[
              {
                role: [USERS.BUSINESS_ADMIN, USERS.BUSINESS_USER],
                component: RenewPlan,
              },
            ]}
          />
          <Route path={ROUTES.RENEW} component={renewRoutes} />
        </Switch>
      </Switch>
      <Switch>
        <LandingRoute exact path="/" component={LandingPage} />
        <AuthPageRoute path="/login" component={Login} loginContainer={false} />
        <AuthPageRoute
          path={ROUTES.PASSWORD_RESET}
          component={ResetPassword}
          loginContainer={false}
        />
        <Route path={ROUTES.NEW_PASSWORD} component={UpdatePassword} />
        <Route path="/terms-of-use" component={TermsOfUse} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path={ROUTES.SIGNUP} component={signUpRoutes} />
        <Route
          exact
          path={ROUTES.VALIDATE_BUSINESS_USER_INVITATION}
          component={BusinessUserInvitation}
        />
        <Route
          exact
          path={ROUTES.PUBLIC_CUSTOM_SURVEY_REPORTS}
          component={BusinessCustomSurveyReport}
        />
        <Route exact path={ROUTES.USER_NEW} component={AddNewBusinessUser} />
        <Route exact path={ROUTES.SOONO_SOCIAL} component={SoonoSocial} />
        <Route exact path="/upgrade-modal" component={UpgradeModal} />
        <LandingRoute exact path="/home" component={LandingPage} />
      </Switch>
    </div>
  </BrowserRouter>
);

export default App;



// WEBPACK FOOTER //
// ./src/containers/App.js